import { ScanLine } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/design/utils';
import type { ScanCaptureState } from '@/features/scan/wedge';

/**
 * The persistent "scanner ready" indicator (AD-32).
 *
 * **It exists because the capture is invisible.** A wedge scanner needs no field to be focused and
 * gives no feedback of its own, so without something on screen saying the terminal is listening,
 * an operator whose scan did nothing has no way to tell a broken scanner from a screen that was
 * not accepting one. That is the difference between "try the cable" and "reload the page", and it
 * is a decision somebody makes with a queue in front of them.
 *
 * **`suppressed` is a state rather than an absence**, for the same reason. A dialog or the code
 * field owning the keyboard is correct behaviour, and an indicator that simply went dark there
 * would look like a fault.
 *
 * The state is carried by colour AND by a word, never by colour alone.
 */

const TONE: Readonly<Record<ScanCaptureState, string>> = {
  ready: 'border-success text-success',
  reading: 'border-accent text-accent',
  suppressed: 'border-warning text-warning',
  off: 'border-border text-muted-foreground',
};

export function ScanIndicator({ state }: Readonly<{ state: ScanCaptureState }>): ReactNode {
  const { t } = useTranslation();
  return (
    <p
      role="status"
      data-scan-state={state}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm font-medium',
        TONE[state],
      )}
    >
      <ScanLine aria-hidden className="size-4" />
      {/*
        The label is read but not drawn, and the state is both. An `aria-label` on the element
        would REPLACE its content for a screen reader, which would announce "band scanner" and
        never the one word that changes — and this is a `role="status"`, so what it announces on a
        change is the whole of what it says.
      */}
      <span className="sr-only">{t('scan.indicator.label')}: </span>
      {t(`scan.indicator.${state}`)}
    </p>
  );
}
